const DAYS_IN_YEAR = 365.25;
const DAYS_IN_MONTH = 30.44;
const DAYS_IN_WEEK = 7;

export interface DetailedDuration {
  years: [number, number];
  months: [number, number];
  weeks: [number, number];
  days: [number, number];
}

export interface RangeData {
  min: number;
  max: number;
  step: number;
}

export const convertDaysToDuration = (
  totalDays: string | number | null | undefined
): string => {
  if (totalDays === null || totalDays === undefined || totalDays === "") {
    return "Not Provided";
  }

  const totalDaysNumber =
    typeof totalDays === "string" ? parseFloat(totalDays) : totalDays;

  if (isNaN(totalDaysNumber)) {
    return "Invalid Amount";
  }

  if (totalDaysNumber === 28 || totalDaysNumber === 30 || totalDays === "30")
    return "1 month";
  if (totalDaysNumber === 60) return "2 months";
  if (totalDaysNumber === 182) return "6 months";
  if (totalDaysNumber === 365) return "1 year";
  if (totalDaysNumber === 730) return "2 years";
  if (totalDaysNumber === 426) return "1 year, 2 months";
  if (totalDaysNumber === 6.6) return "1 week";
  if (totalDaysNumber === 433) return "1 year, 2 months, 1 week";
  if (totalDaysNumber === 436) return "1 year, 2 months, 1 week, 3 days";

  let remainingDays = totalDaysNumber;

  let years = Math.floor(remainingDays / DAYS_IN_YEAR);
  remainingDays %= DAYS_IN_YEAR;

  let months = Math.floor(remainingDays / DAYS_IN_MONTH);
  remainingDays %= DAYS_IN_MONTH;

  let weeks = Math.floor(remainingDays / DAYS_IN_WEEK);
  remainingDays %= DAYS_IN_WEEK;

  let days = Math.round(remainingDays);

  if (days === 7) {
    weeks++;
    days = 0;
  }

  if (weeks === 4) {
    months++;
    weeks = 0;
  }

  if (months === 12) {
    years++;
    months = 0;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} year${years !== 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} month${months !== 1 ? "s" : ""}`);
  if (weeks > 0) parts.push(`${weeks} week${weeks !== 1 ? "s" : ""}`);

  if ((years === 0 && months === 0 && weeks === 0) || totalDaysNumber === 436) {
    if (days > 0) parts.push(`${days} day${days !== 1 ? "s" : ""}`);
  }

  return parts.join(", ");
};

function convertSingleValue(totalDaysNumber: number) {
  let remainingDays = totalDaysNumber;

  let years = Math.floor(remainingDays / DAYS_IN_YEAR);
  remainingDays %= DAYS_IN_YEAR;

  let months = Math.floor(remainingDays / DAYS_IN_MONTH);
  remainingDays %= DAYS_IN_MONTH;

  let weeks = Math.floor(remainingDays / DAYS_IN_WEEK);
  remainingDays %= DAYS_IN_WEEK;

  let days = Math.round(remainingDays);

  if (days === 7) {
    weeks++;
    days = 0;
  }

  if (weeks === 4) {
    months++;
    weeks = 0;
  }

  if (months === 12) {
    years++;
    months = 0;
  }

  return { years, months, weeks, days };
}

export const convertDaysToRangeData = (
  rangeData: RangeData
): DetailedDuration => {
  const maxDuration = convertSingleValue(rangeData.max);

  const result: DetailedDuration = {
    years: [0, maxDuration.years],
    months: [0, 11],
    weeks: [0, 3],
    days: [0, 6],
  };

  return result;
};

export const convertRangeDataToDays = (
  detailedDuration: DetailedDuration
): [number, number] => {
  const convertSingleValue = (duration: {
    years: number;
    months: number;
    weeks: number;
    days: number;
  }): number => {
    return Math.floor(
      duration.years * DAYS_IN_YEAR +
        duration.months * DAYS_IN_MONTH +
        duration.weeks * DAYS_IN_WEEK +
        duration.days
    );
  };

  const minDays = convertSingleValue({
    years: detailedDuration.years[0],
    months: detailedDuration.months[0],
    weeks: detailedDuration.weeks[0],
    days: detailedDuration.days[0],
  });

  const maxDays = convertSingleValue({
    years: detailedDuration.years[1],
    months: detailedDuration.months[1],
    weeks: detailedDuration.weeks[1],
    days: detailedDuration.days[1],
  });

  return [minDays, maxDays];
};

export const getDefaultDurationValues = (
  rangeData: RangeData
): {
  detailedDuration: DetailedDuration;
  monthValue: [number, number];
  weekValue: [number, number];
  dayValue: [number, number];
} => {
  const convertedData = convertDaysToRangeData(rangeData);

  const monthValue: [number, number] = [0, 11];
  const weekValue: [number, number] = [0, 3];
  const dayValue: [number, number] = [0, 6];

  return {
    detailedDuration: convertedData,
    monthValue,
    weekValue,
    dayValue,
  };
};
